import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Responsive, WidthProvider, Layout } from 'react-grid-layout';
import UsersService from 'api/users/UsersService';
import useDashboardLayout from '../hooks/useDashboardLayout';
import { WIDGET_REGISTRY, isWidgetInGroup, effectivePolicyRequired } from '../widgetRegistry';
import { DashboardGroup, WidgetCategory, WidgetDefinition } from '../types/DashboardLayoutTypes';
import WidgetWrapper from './WidgetWrapper';
import WidgetPickerDrawer from './WidgetPickerDrawer';
import DashboardEmptyState from './DashboardEmptyState';
import AddMorePlaceholder from './AddMorePlaceholder';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { WidgetCategoryContext } from './WidgetCategoryContext';
import { DashboardVehicleScopeContext } from '../hooks/useDashboardVehicleScope';
import { LG_MIN_WIDTH_COLS, XXL_BREAKPOINT_PX } from '../utils/gridConstants';
import styles from '../DashboardScreen.module.scss';
import gridStyles from '../DashboardGrid.module.scss';

const ResponsiveGridLayout = WidthProvider(Responsive);

const ROW_HEIGHT = 65;

type Props = {
	// Resultado do hook de layout (painel principal ou ativo).
	layoutApi: ReturnType<typeof useDashboardLayout>;
	userPolicies: string[];
	dashboardGroup: DashboardGroup;
	enabledCategories: WidgetCategory[];
	// Quando definido, restringe os widgets visíveis e o picker a este conjunto (ex.: dashboard do ativo).
	availableWidgetIds?: Set<string>;
	// Constrói as props injetadas em cada widget (filters/vehicles/drivers/vehicleId/lockedVehicleId…).
	buildWidgetProps: (def: WidgetDefinition) => Record<string, unknown>;
	isDrawerOpen: boolean;
	setIsDrawerOpen: (open: boolean) => void;
	chartsAreaClassName?: string;
	// Âmbito de ativo: quando definido, os widgets juntam este vehicleId à sua criteria
	// (mantendo o seu próprio seletor de período). Undefined no painel principal.
	vehicleScopeId?: string;
	// Empty-state: label do botão primário e ação secundária (lado a lado). Opcionais —
	// o painel principal não os passa (mantém "+ Ajouter" sem ação secundária).
	emptyStatePrimaryLabel?: string;
	emptyStateAction?: { label: string; onClick: () => void };
};

const DashboardCanvas = ({
	layoutApi,
	userPolicies,
	dashboardGroup,
	enabledCategories,
	availableWidgetIds,
	buildWidgetProps,
	isDrawerOpen,
	setIsDrawerOpen,
	chartsAreaClassName,
	vehicleScopeId,
	emptyStatePrimaryLabel,
	emptyStateAction,
}: Props) => {
	const {
		layout,
		isEditMode,
		setIsEditMode,
		updatePositions,
		addWidget,
		removeWidget,
		isLoading: isLoadingLayout,
	} = layoutApi;

	// Contexto do ativo: quando há vehicleScopeId, os widgets com `assetPolicyRequired` usam essa
	// política em vez da geral (ver effectivePolicyRequired).
	const isAssetContext = !!vehicleScopeId;

	const isInScope = useCallback(
		(widgetId: string) => !availableWidgetIds || availableWidgetIds.has(widgetId),
		[availableWidgetIds],
	);

	const visibleWidgets = useMemo(() => {
		return layout.filter(w => {
			const def = WIDGET_REGISTRY[w.i];
			if (!def) return false;
			if (!w.visible) return false;
			// Widgets `assetOnly` só aparecem no dashboard do ativo, nunca no painel geral.
			if (def.assetOnly && !isAssetContext) return false;
			if (!isInScope(w.i)) return false;
			if (!enabledCategories.includes(def.category)) return false;
			const policyRequired = effectivePolicyRequired(def, isAssetContext);
			if (policyRequired && !UsersService.hasPolicies(userPolicies, [policyRequired as any])) return false;
			if (!isWidgetInGroup(w.i, dashboardGroup)) return false;
			return true;
		});
	}, [layout, enabledCategories, userPolicies, dashboardGroup, isInScope, isAssetContext]);

	const [isBelowXxl, setIsBelowXxl] = useState(
		() => typeof window !== 'undefined' && window.innerWidth <= XXL_BREAKPOINT_PX
	);

	useEffect(() => {
		const handleResize = () => {
			setIsBelowXxl(window.innerWidth <= XXL_BREAKPOINT_PX);
		};
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const gridLayout = useMemo<Layout[]>(() => {
		return visibleWidgets.map(w => {
			const baseMinW = w.minW ?? WIDGET_REGISTRY[w.i]?.minW ?? 3;
			// Widgets that explicitly declare minW < LG floor opt out of the sub-xxl width floor.
			const applyLgFloor = isBelowXxl && baseMinW >= LG_MIN_WIDTH_COLS;
			return {
				i: w.i,
				x: w.x,
				y: w.y,
				w: applyLgFloor ? Math.max(w.w, LG_MIN_WIDTH_COLS) : w.w,
				h: w.h,
				minW: applyLgFloor ? Math.max(baseMinW, LG_MIN_WIDTH_COLS) : baseMinW,
				minH: w.minH ?? WIDGET_REGISTRY[w.i]?.minH ?? 2,
			};
		});
	}, [visibleWidgets, isBelowXxl]);

	// --- Edit mode: hover + grab approach ---
	const editModeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const manualEditRef = useRef(false);

	const onInteractionStart = useCallback(() => {
		if (editModeTimeoutRef.current) {
			clearTimeout(editModeTimeoutRef.current);
			editModeTimeoutRef.current = null;
		}
		if (!isEditMode) setIsEditMode(true);
	}, [isEditMode, setIsEditMode]);

	const onInteractionStop = useCallback((newLayout: Layout[]) => {
		updatePositions(newLayout, isBelowXxl);
		if (!manualEditRef.current) {
			editModeTimeoutRef.current = setTimeout(() => setIsEditMode(false), 1500);
		}
	}, [updatePositions, setIsEditMode, isBelowXxl]);

	useEffect(() => {
		return () => {
			if (editModeTimeoutRef.current) clearTimeout(editModeTimeoutRef.current);
		};
	}, []);

	const totalWidgets = useMemo(() =>
		Object.entries(WIDGET_REGISTRY).filter(([id, def]) => {
			const policyRequired = effectivePolicyRequired(def, isAssetContext);
			return (!def.assetOnly || isAssetContext)
				&& isInScope(id)
				&& enabledCategories.includes(def.category)
				&& (!policyRequired || UsersService.hasPolicies(userPolicies, [policyRequired as any]))
				&& isWidgetInGroup(id, dashboardGroup);
		}).length,
		[enabledCategories, userPolicies, dashboardGroup, isInScope, isAssetContext]);

	const hasWidgets = visibleWidgets.length > 0;

	return (
		<DashboardVehicleScopeContext.Provider value={vehicleScopeId}>
			{isLoadingLayout ? (
				// Enquanto não sabemos QUAIS widgets mostrar, não renderizar o layout por defeito
				// (evita o "flash" de todos os widgets antes de chegar a disposição gravada).
				<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4rem' }}>
					<LoadingSpinner />
				</div>
			) : !hasWidgets ? (
				<DashboardEmptyState
					onAddClick={() => setIsDrawerOpen(true)}
					totalWidgets={totalWidgets}
					primaryLabel={emptyStatePrimaryLabel}
					secondaryAction={emptyStateAction}
				/>
			) : (
				<>
					<div className={`${styles.chartsArea} ${chartsAreaClassName ?? ''} ${gridStyles.gridContainer} ${isEditMode ? gridStyles.editMode : ''}`}>
						<ResponsiveGridLayout
							layouts={{ lg: gridLayout }}
							breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
							cols={{ lg: 12, md: 12, sm: 6, xs: 1 }}
							rowHeight={ROW_HEIGHT}
							onDragStart={onInteractionStart}
							onResizeStart={onInteractionStart}
							onDragStop={onInteractionStop}
							onResizeStop={onInteractionStop}
							isDraggable={true}
							isResizable={true}
							draggableHandle=".widget-drag-handle"
							margin={[16, 10]}
							containerPadding={[16, 4]}
						>
							{visibleWidgets.map(w => {
								const def = WIDGET_REGISTRY[w.i];
								if (!def) return null;
								const WidgetComponent = def.component;
								return (
									<div key={w.i}>
										<WidgetCategoryContext.Provider value={def.category}>
											<WidgetWrapper
												isEditMode={isEditMode}
												onRemove={() => removeWidget(w.i)}
											>
												<WidgetComponent {...buildWidgetProps(def)} />
											</WidgetWrapper>
										</WidgetCategoryContext.Provider>
									</div>
								);
							})}
						</ResponsiveGridLayout>
					</div>
					<AddMorePlaceholder onClick={() => setIsDrawerOpen(true)} />
				</>
			)}
			<WidgetPickerDrawer
				isOpen={isDrawerOpen}
				onClose={() => setIsDrawerOpen(false)}
				layout={layout}
				onAddWidget={addWidget}
				onRemoveWidget={removeWidget}
				enabledCategories={enabledCategories}
				userPolicies={userPolicies}
				dashboardGroup={dashboardGroup}
				availableWidgetIds={availableWidgetIds}
					isAssetScope={!!vehicleScopeId}
			/>
		</DashboardVehicleScopeContext.Provider>
	);
};

export default DashboardCanvas;
